import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MapInfoWindow, GoogleMap } from '@angular/google-maps';
import { TranslateService } from '@ngx-translate/core';
import { RequestService, GoogleMapsLoaderService } from '../../services';
import { environment } from 'src/environments/environment';
import { Subscription } from 'rxjs';
import { getDefaultIconForMarkerType } from './data-prepare';
import { GeocodingService } from '../../services/geocoding.service';
import { MessagingService } from '../../services/messaging.service';
import { GeoUtils } from '../../utils/geo-utils';

declare const google: any;
@Component({
    selector: 'app-google-map',
    templateUrl: './google-map.component.html',
    styleUrls: ['./google-map.component.scss']
})
export class GoogleMapsComponent implements OnInit {
    private subscriptions: Subscription[] = [];
    display: any;
    center: google.maps.LatLngLiteral = environment.lnglatdefault;
    zoom = 15;
    googleInit = false;
    options = {
        streetViewControl: false,
        mapTypeControl: true,
        fullscreenControl: false,
        minZoom: 3,
        disableDoubleClickZoom: false,
        panControl: true,
        draggable: true,
        clickableIcons: false,
        keyboardShortcuts: false,
        scaleControl: true,
        styles: []
    }
    markersOriginal: any[] = [];
    markersOriginalAdjusted: any[] = [];
    markers: any[] = [];
    usersMarkers: any[] = [];
    marker: any = {};
    markerClustererImagePath: string = 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m';
    showCluster: boolean = false;
    geoLocationInit: boolean = false;
    geocoder: any;
    defaultMarkerIcon: string = undefined;
    private currentUser: any = undefined;
    public isAdmin: boolean = false;
    public isSuperAdmin: boolean = false;
    public isCommander: boolean = false;
    polylines: Map<string, { line: google.maps.Polyline; pointMarkers: google.maps.Marker[] }> = new Map();
    polylinesMain: google.maps.LatLngLiteral[][] = []
    polylinePath: google.maps.LatLngLiteral[] = [];
    polylineOptions: google.maps.PolylineOptions = {
        path: this.polylinePath,
        visible: true
    };
    observer: IntersectionObserver;
    @Input() lazyLoad: boolean = false;
    @Output() shapeClick = new EventEmitter<any>(); // Emits ID or Data of clicked shape
    // Track markers that were recently manually updated to prevent realtime updates from overwriting them
    private recentlyUpdatedMarkers: Map<string, { timestamp: number, position: { lat: number, lng: number } }> = new Map();
    private readonly MANUAL_UPDATE_TIMEOUT = 2000; // 2 seconds
    // Track NATS subjects per marker key for cleanup, mirroring offline-map
    private realtimeListeners: Map<string, string> = new Map();

    public selectedMarker = 'Info Window content';

    // Interactive Shapes
    private sectors = new Map<string, {
        id: string;
        center: { lat: number, lng: number };
        radius: number;
        azimuth: number;
        fov: number;
        polygon?: any; // google.maps.Polygon
        centerMarker?: any; // google.maps.Marker
        controlMarker?: any; // google.maps.Marker
    }>();

    private boxes = new Map<string, {
        id: string;
        center: { lat: number, lng: number };
        width: number;
        height: number;
        azimuth: number;
        polygon?: any; // google.maps.Polygon
        centerMarker?: any; // google.maps.Marker
        controlMarker?: any; // google.maps.Marker
    }>();

    private lineSymbol = {
        path: "M 0,-1 0,1",
        strokeOpacity: 1,
        scale: 6,
    };
    private readonly draftSafeRoutePolylineIds = new Set(['case-map-draw-polyline', 'dashboard-map-draw-polyline']);

    @Input()
    set styles(styles: any) {
        // if (styles?.length) {
        // debugger;
        if (this.markersElem) {
            if (this.options)
                this.options.styles = styles;
            this.markersElem.googleMap.setOptions(this.options);
        }
        // }
    }
    @Input() currentGeoLocation: any = undefined;
    @Input() width: string = '100%';
    @Input() height: string = '30vh';
    @Input() enableTrackMarkers: boolean = false;
    @Input() caseId: string = undefined;
    private trackingMarkers: string[] = ['investigator', 'investigator-group'];
    @Input() addMyMarker: boolean = true;
    @Input() isClickable: boolean = true;
    @Input() mapDragGeoEnabled: boolean = false;
    @Input() isHoverable: boolean = false;
    /** When false, marker caption labels are hidden (same behavior as offline map). */
    @Input() isIconLabelsOn: boolean = true;
    @Input() enableAddessOnClick: boolean = false;
    @Input()
    set isDraggable(isDraggable: boolean) {
        this.options.draggable = isDraggable;
    }
    @Input()
    @Input()
    set setGeoLocation(geoLocation: any) {
        if (geoLocation && geoLocation !== '') {
            if (typeof geoLocation === 'string') {
                try {
                    this.currentGeoLocation = JSON.parse(geoLocation);
                } catch (e) {
                    console.error('Error parsing setGeoLocation', e);
                    return;
                }
            } else {
                this.currentGeoLocation = geoLocation;
            }

            if (!this.geoLocationInit) {
                this.center = this.currentGeoLocation;
                this.geoLocationInit = true;
            }
            this.setPin(this.currentGeoLocation);
        } else {
            this.currentGeoLocation = undefined;
        }
    }
    @Input()
    @Input()
    set setCenter(geoLocation: any) {
        if (geoLocation && geoLocation !== '') {
            if (typeof geoLocation === 'string') {
                try {
                    this.currentGeoLocation = JSON.parse(geoLocation);
                } catch (e) {
                    console.error('Error parsing setCenter', e);
                    return;
                }
            } else {
                this.currentGeoLocation = geoLocation;
            }

            if (!this.geoLocationInit) {
                this.center = this.currentGeoLocation;
                this.geoLocationInit = true;
            }
        }
    }
    @Input()
    @Input()
    set changeCenter(geoLocation: any) {
        if (geoLocation && geoLocation !== '') {
            if (typeof geoLocation === 'string') {
                try {
                    this.currentGeoLocation = JSON.parse(geoLocation);
                } catch (e) {
                    console.error('Error parsing changeCenter', e);
                    return;
                }
            } else {
                this.currentGeoLocation = geoLocation;
            }
            this.center = this.currentGeoLocation;
        }
    }
    @Input()
    @Input()
    set changeCenterAddPin(geoLocation: any) {
        if (geoLocation && geoLocation !== '') {
            if (typeof geoLocation === 'string') {
                try {
                    this.currentGeoLocation = JSON.parse(geoLocation);
                } catch (e) {
                    console.error('Error parsing changeCenterAddPin', e);
                    return;
                }
            } else {
                this.currentGeoLocation = geoLocation;
            }

            this.center = this.currentGeoLocation;
            this.setPin(this.currentGeoLocation);
        }
    }
    @Input()
    set markerIcon(icon: string) {
        if (icon) {
            this.defaultMarkerIcon = icon;
            if (this.currentGeoLocation)
                this.setPin(this.currentGeoLocation);
        }
    }
    @Input()
    set setMapTypeControl(mapTypeControl: boolean) {
        this.options.mapTypeControl = mapTypeControl;
    }
    @Input()
    set setScaleControl(scaleControl: boolean) {
        this.options.scaleControl = scaleControl;
    }

    @Output() address = new EventEmitter<string>();
    @Output() geoLocation = new EventEmitter<any>();
    @Output() markerSelect = new EventEmitter<any>();
    @Output() markerInfoSelect = new EventEmitter<any>();
    @Output() markerDelete = new EventEmitter<any>();
    @Output() markerDragEnd = new EventEmitter<any>();
    @Output() markerRightClick = new EventEmitter<any>();
    @Output() mapSelect = new EventEmitter<any>();
    @Output() mapInitialized = new EventEmitter<any>();
    @Output() mapReady = new EventEmitter<any>();
    @Output() tilesloaded = new EventEmitter<any>();
    @Output() addressPinData = new EventEmitter<any>();
    @Output() addressJPPinData = new EventEmitter<any>();
    @Output() mapDragGeo = new EventEmitter<any>();

    @ViewChild(MapInfoWindow) infoWindow: MapInfoWindow;
    @ViewChild(GoogleMap) markersElem: GoogleMap;
    constructor(
        private translate: TranslateService,
        private elementRef: ElementRef,
        private requestService: RequestService,
        private googleMapsLoader: GoogleMapsLoaderService,
        private geocodingService: GeocodingService,
        private natsMessaging: MessagingService
    ) {
        this.googleMapsLoader.waitForGoogleMaps().then(() => {
            this.googleInit = true;
            this.geocoder = new google.maps.Geocoder();
            this.initialize();
        }).catch(err => console.error("Google Maps failed to load:", err));
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.requestService.currentUserSubject.subscribe((data) => {
                if (data) {
                    this.currentUser = data;
                    this.isSuperAdmin = this.requestService.isUserRoleSuperAdmin();
                    this.isAdmin = this.requestService.isUserRoleAdmin();
                    this.isCommander = this.requestService.isUserRoleCommander();
                }
            })
        );
    }
    initialize(): void {
        if (this.currentGeoLocation) {
            this.center = this.currentGeoLocation;
            return;
        }
        if (this.geoLocationInit) {
            return;
        }
        if (navigator.geolocation) {
            try {
                navigator.geolocation.getCurrentPosition((position: any) => {
                    if (!this.currentGeoLocation) {
                        this.center = {
                            lat: position.coords.latitude,
                            lng: position.coords.longitude
                        }
                        this.geocodeLatLng(this.geocoder, position.coords.latitude, position.coords.longitude);
                        if (this.addMyMarker) {
                            this.marker = {
                                position: this.center,
                                // label: {
                                //     color: 'blue',
                                //     text: 'Marker label ',
                                // },
                                title: this.translate.instant('My Pin'),
                                info: 'My Marker info',
                                options: {
                                    animation: google.maps.Animation.DROP,
                                },
                            }

                            if (this.defaultMarkerIcon) {
                                this.marker.icon = this.defaultMarkerIcon;
                            }
                        }
                        if (this.markersElem && this.mapDragGeoEnabled) {
                            this.mapDragGeo.emit(this.markersElem.getCenter().toJSON());
                        }
                    }
                }, (error: any) => {
                    this.makeNoLocationReady(error);
                });
            }
            catch (e) {
                this.makeNoLocationReady();
            }
        } else {
            this.makeNoLocationReady();
        }
    }
    makeNoLocationReady(error = undefined) {
        let lng = 138.2529;
        let lat = 36.2048;
        this.center = {
            lat: lat,
            lng: lng
        }
        this.zoom = 4;
        this.geocodeLatLng(this.geocoder, lat, lng);
        if (error) {

            switch (error.code) {
                case error.PERMISSION_DENIED:
                    console.info("User denied the request for Geolocation.");
                    // alert("Please enable location permissions in your browser settings.");
                    break;
                case error.POSITION_UNAVAILABLE:
                    console.error("Location information is unavailable.");
                    // alert("Unable to determine your location. Try again later.");
                    break;
                case error.TIMEOUT:
                    console.error("The request to get user location timed out.");
                    // alert("Location request timed out. Please try again.");
                    break;
                case error.UNKNOWN_ERROR:
                default:
                    console.error("An unknown error occurred.");
                    // alert("An unknown error occurred while fetching location.");
                    break;
            }
        }
    }
    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.unTrackMarkers(this.trackingMarkers);
        // Clean up any remaining realtime listeners
        this.realtimeListeners.forEach((subject: string) => {
            this.natsMessaging.unsubscribe('', subject);
        });
        this.realtimeListeners.clear();
    }
    ngAfterViewInit() {
        if (this.markersElem && this.mapDragGeoEnabled) {
            this.mapDragGeo.emit(this.markersElem.getCenter().toJSON());
        }
        if (this.lazyLoad) {
            this.observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        setTimeout(() => {
                            this.lazyLoad = false;
                        }, 300)
                        this.observer.disconnect();
                    }
                });
            }, {
                threshold: 0.1 // Adjust this threshold as needed
            });

            this.observer.observe(this.elementRef.nativeElement);
        }

    }

    // moveMap(event: google.maps.MapMouseEvent) {
    //     if (event.latLng != null) this.center = (event.latLng.toJSON());
    // }

    // move(event: google.maps.MapMouseEvent) {
    //     if (event.latLng != null) this.display = event.latLng.toJSON();
    // }
    tilesload(event: any) {
        this.tilesloaded.emit(event);
    }
    mapInitialize(event: any) {
        this.mapInitialized.emit(event);
    }
    onMapReady(event: any) {
        this.mapReady.emit(event);
    }
    mapClick(event: any) {
        if (this.isClickable) {
            let lat = event.latLng.lat();
            let lng = event.latLng.lng();
            if (this.enableAddessOnClick) {
                this.reverseGeocode(lat, lng);
            }
            this.mapSelect.emit({ lat: lat, lng: lng });
        }
    }
    mapDrag(event: any) {
        if (this.markersElem && this.mapDragGeoEnabled) {
            this.mapDragGeo.emit(this.markersElem.getCenter().toJSON());
        }
    }
    onCenterChange(event: google.maps.MapMouseEvent) {
        // this.center = event.latLng.toJSON();
        console.log('Map center:', event.latLng.toJSON());
        // You can perform any additional actions here with the new center coordinates
    }
    markerHover(event: any, mark: any, marker) {
        if (this.isHoverable) {
            this.selectedMarker = mark;
            if (!mark.hideInfo) {
                this.infoWindow.open(marker);
            }
        }
    }
    private canManageSquadMarkers(): boolean {
        // return this.isAdmin || this.isSuperAdmin || this.isCommander;
        return true;
    }
    markerHoverOut(event: any) {
        if (this.isHoverable) {
            this.infoWindow.close();
        }
    }
    markerClick(event: any, mark: any, marker) {
        if (this.isClickable) {
            // For squad and teammember markers, show context menu on left-click
            // if (mark && (mark.markerType === 'squad' || mark.markerType === 'teammember') && this.canManageSquadMarkers()) {
            //     this.markerRightClick.emit({
            //         marker: mark,
            //         event: event?.domEvent || event,
            //         latLng: event?.latLng
            //     });
            // }
            this.markerSelect.emit(mark);
            this.selectedMarker = mark;
            if (!mark.hideInfo) {
                this.infoWindow.open(marker);
            }
        }
    }
    selectMarkerInfo() {
        if (this.isClickable) {
            this.markerInfoSelect.emit(this.selectedMarker);
        }
    }
    deleteMarker(marker: any) {
        if (!this.canManageSquadMarkers()) {
            return;
        }
        if (marker && (marker.markerType === 'squad' || marker.markerType === 'teammember')) {
            this.markerDelete.emit(marker);
            this.infoWindow.close();
        }
    }
    onMarkerRightClick(event: any, mark: any, marker: any) {
        if (event?.domEvent) {
            event.domEvent.preventDefault();
        }
        // Right-click now just prevents default, context menu is shown on left-click
    }
    onMarkerDragEnd(event: any, mark: any, marker: any) {
        if (!this.canManageSquadMarkers()) {
            return;
        }
        if (mark && (mark.markerType === 'squad' || mark.markerType === 'teammember')) {
            this.markerDragEnd.emit({
                marker: mark,
                latLng: event.latLng
            });
        }
    }
    setPin(event: any) {
        if (this.isClickable) {
            // event.latLng.lat()
            // event.latLng.lng()
            this.geocodeLatLng(this.geocoder, event.lat, event.lng);
            this.marker = {
                position: {
                    lat: event.lat,
                    lng: event.lng,
                },
                // label: {
                //   color: 'blue',
                //   text: 'Marker label ' + (this.markers.length + 1),
                // },
                title: this.translate.instant('Pin'),
                info: 'Marker info',
                hideInfo: true,
                // options: {
                //     animation: google.maps.Animation.DROP,
                // },
            }

            if (this.defaultMarkerIcon) {
                this.marker.icon = this.defaultMarkerIcon;
            }
        }
    }

    geocodeLatLng(
        geocoder: google.maps.Geocoder, lat: any, lng: any
    ) {
        const latlng = {
            lat: lat,
            lng: lng,
        };

        this.geocodingService.resolveAddress(lat, lng)
            .then(address => {
                if (address) {
                    this.address.emit(address);
                }
                this.geoLocation.emit(latlng);
            })
            .catch(() => {
                this.geoLocation.emit(latlng);
            });
    }
    getHideInfo(mark) {
        let status = false;
        if (mark.hideInfo) {
            return true;
        }
        // || mark.markerType === 'note'
        if (mark.markerType === 'document') {
            let description = mark.description ? mark.description : mark.notes ? mark.notes : ''
            if (description === '')
                return true;
        } else if (mark.markerType === 'note') {
            let description = mark.description ? mark.description : '';
            if (description == '' && !(mark.person_of_interest || mark.car_of_interest)) {
                // console.log(true);
                return true;
            }
        } else if (mark.markerType === 'location') {
            return true;
        } else if (mark.markerType === 'squad') {
            return true;
        }
        return status;
    }
    prepareMarker(mark) {
        let marker = {
            position: {
                lat: mark.lat,
                lng: mark.lng,
            },
            geolocation: mark.geolocation,
            email: mark.email,
            lat: mark.lat,
            lng: mark.lng,
            delete: mark.delete,
            // label: {
            //     color: 'blue',
            //     text: 'Marker label ' + (this.markersOriginal.length + 1),
            // },
            dataType: mark.type ? mark.type : mark.dataType,
            investigators: mark.investigators ? mark.investigators : undefined,
            investigatorgroup: mark.investigatorgroup ? mark.investigatorgroup : undefined,
            cameras: mark.cameras ? mark.cameras : undefined,
            // type: mark.type,
            markerType: mark.markerType,
            _id: mark._id,
            title: mark.name,
            // label: mark.name,
            hideInfo: this.getHideInfo(mark),
            showLabel: mark.showLabel,
            label: this.prepareLabel(mark),
            name: mark['alternative_name'] ? mark['alternative_name'] : mark['name'],
            camera_id: mark.camera_id,
            case_id: mark.case_id,
            case: mark.case,
            status: mark.status,
            pictureLink: mark.pictureLink ? mark.pictureLink : mark.picture ? mark.picture : 'assets/images/error-default-image.png',
            picture: mark.picture ? mark.picture : mark.pictureLink ? mark.pictureLink : 'assets/images/error-default-image.png',
            picture_icon: mark.picture_icon ? mark.picture_icon : mark.picture ? mark.picture : mark.pictureLink ? mark.pictureLink : 'assets/images/error-default-image.png',
            icon: mark.icon ? mark.icon : '',
            description: mark.description ? mark.description : mark.notes ? mark.notes : '',
            rank: mark.rank ? mark.rank : '',
            years_in_service: mark.years_in_service ? mark.years_in_service : '',
            info: 'Marker info ' + (this.markersOriginal.length + 1),
            // options: {
            //     animation: google.maps.Animation.DROP,
            // },
            createdAt: mark.createdAt,
            note: mark,
            hideIt: (this.enableTrackMarkers && this.trackingMarkers.includes(mark.markerType)) ? true : false
        };
        // if (mark['icon']) {
        //     marker['icon'] = mark['icon'];
        // }

        // For squad and teammember markers, only allow dragging if user is admin or superadmin
        const isSquadOrTeammemberMarker = mark.markerType === 'squad' || mark.markerType === 'teammember';
        const canDragMarker = isSquadOrTeammemberMarker ? this.canManageSquadMarkers() : true;
        const shouldBeDraggable = mark.draggable === true && canDragMarker;

        const defaultIconUrl = getDefaultIconForMarkerType(mark.markerType);
        // Use custom icon if provided, otherwise use default
        let iconUrlToUse = mark.icon || defaultIconUrl;

        let iconObject = {
            url: iconUrlToUse,
            // scaledSize: new google.maps.Size(70, 80)
        };

        // If custom icon is provided, validate it and fallback to default if it fails
        if (mark.icon) {
            // Validate icon URL asynchronously
            const img = new Image();
            img.onload = () => {
                // Icon exists and loaded successfully, no need to update (already using it)
            };
            img.onerror = () => {
                // Icon failed to load, switch to default icon
                const defaultIconObject = {
                    url: defaultIconUrl
                };
                // Find and update the marker in all relevant arrays
                const updateMarkerIcon = (markerToUpdate: any) => {
                    if (markerToUpdate) {
                        markerToUpdate['icon'] = defaultIconObject;
                        if (markerToUpdate['options']) {
                            markerToUpdate['options'].icon = defaultIconObject;
                        } else {
                            markerToUpdate['options'] = {
                                icon: defaultIconObject,
                                draggable: shouldBeDraggable
                            };
                        }
                    }
                };

                // Update in markersOriginal
                const markerInOriginal = this.markersOriginal.find(m => m._id === marker._id);
                updateMarkerIcon(markerInOriginal);

                // Update in markersOriginalAdjusted
                const markerInAdjusted = this.markersOriginalAdjusted.find(m => m._id === marker._id);
                updateMarkerIcon(markerInAdjusted);

                // Update in markers (display array)
                const markerInDisplay = this.markers.find(m => m._id === marker._id);
                updateMarkerIcon(markerInDisplay);
            };
            img.src = mark.icon;
        }
        marker['icon'] = iconObject;
        marker['options'] = {
            icon: iconObject,
            draggable: shouldBeDraggable
        };
        // for (let item of this.markersOriginal) {
        //     if (this.markersCoincide(item, marker)) {
        //         marker = this.moveMarkerRandomly(marker);
        //         break;
        //     }
        // }
        return marker;
    }

    /**
     * Log markers currently added to the Google map.
     * If an ID is provided, only markers matching that ID are logged.
     */
    public logMarkersToConsole(id: string): any {
        const allMarkers = this.markersOriginal || [];
        const markersToLog = id
            ? allMarkers.filter((m) => m && (m._id === id || m.id === id))
            : allMarkers;

        if (!markersToLog.length) {
            console.log(`[GoogleMap] No markers found with id "${id}".`);
            return undefined;
        }

        const summary = markersToLog.map((m, index) => ({
            index,
            id: m._id,
            type: m.markerType,
            title: m.title,
            lat: m.lat,
            lng: m.lng,
            geolocation: m.geolocation,
            hidden: m.hideInfo || m.hideIt,
            delete: m.delete,
            caseId: m.case_id,
            status: m.status,
            createdAt: m.createdAt
        }));

        return summary;
    }
    public showMarkersIfReady(typeToHide: string[] = [], calculateCenterZoom: boolean = true): void {
        if (!this.markersElem.googleMap) {
            return;
        }
        this.showMarkers(typeToHide, calculateCenterZoom);
    }
    prepareLabel(mark) {
        let label = {};
        if (typeof mark.label === "object") {
            label = mark.label;
        } else {
            label = mark.label ? {
                text: this.transform(mark.label, ['50', '...']),
                color: mark.labelColor ? mark.labelColor : 'black',
                fontSize: '12px',
                fontWeight: 'bold',
                className: 'titleMarkClass'
            } : mark.name ? {
                text: this.transform(mark.name, ['50', '...']),
                color: mark.labelColor ? mark.labelColor : 'black',
                fontSize: '12px',
                fontWeight: 'bold',
                className: 'titleMarkClass'
            } : mark.case_id ? {
                text: this.transform(mark.case_id, ['50', '...']),
                color: mark.labelColor ? mark.labelColor : 'black',
                fontSize: '12px',
                fontWeight: 'bold',
                className: 'titleMarkClass'
            } : undefined;
        }

        return label;
    }
    moveMarkerRandomly(mark) {
        const dx = Math.random() * 0.0001; // Adjust the range as needed
        const dy = Math.random() * 0.0001;
        const newLat = mark.position.lat + dx;
        const newLng = mark.position.lng + dy;
        mark.position.lat = newLat;
        mark.position.lng = newLng;
        return mark;
    }
    markersCoincide(marker1, marker2) {
        const mark1 = new google.maps.LatLng(marker1.position.lat, marker1.position.lng);
        const mark2 = new google.maps.LatLng(marker2.position.lat, marker2.position.lng);
        const distance = google.maps.geometry.spherical.computeDistanceBetween(mark1, mark2);
        return (marker1.position.lat === marker2.position.lat && marker1.position.lng === marker2.position.lng) || distance < 4;
    }
    addMarkers(markers) {
        this.markersOriginal = [];
        // console.log('markers', markers);
        for (let mark of markers) {
            // if (mark.lat && mark.lat !== '' && mark.lng && mark.lng !== '') {
            let marker = this.prepareMarker(mark);
            this.markersOriginal.push(marker);
            // }
        }
        // console.log('markersOriginal', this.markersOriginal)
        this.showMarkersIfReady();
    }
    updateMarkers(newMarkers = [], calculateCenterZoom: boolean = true, callback: (markers: any[]) => void = undefined) {
        // console.log('markers', markers);
        // let markersOriginal = JSON.parse(JSON.stringify(this.markersOriginal));
        // console.log('this.markersOriginal', markersOriginal);
        for (let mark of newMarkers) {
            // if (mark.lat && mark.lat !== '' && mark.lng && mark.lng !== '') {
            let marker = this.prepareMarker(mark);
            this.updateOriginalMarker(marker);
            // }
        }
        // markersOriginal = JSON.parse(JSON.stringify(this.markersOriginal));
        // console.log('this.markersOriginal', markersOriginal);
        this.showMarkersIfReady([], calculateCenterZoom);
        if (callback) {
            callback(this.markersOriginal);
        }
    }

    trackByMarkerId(index: number, marker: any): string | number {
        return marker?._id || marker?.id || `${marker?.markerType || 'marker'}_${index}`;
    }

    setMarkerVisibilityByIds(ids: string[] = [], visible: boolean = true, callback: (markers: any[]) => void = undefined): void {
        const markerIds = new Set((ids || []).filter(Boolean));
        if (!markerIds.size) return;

        const setVisibility = (marker: any) => {
            if (!marker?._id || !markerIds.has(marker._id)) return marker;
            return { ...marker, hideIt: !visible, mapToolsHidden: !visible };
        };

        this.markersOriginal = this.markersOriginal.map(setVisibility);
        this.markersOriginalAdjusted = this.markersOriginalAdjusted.map(setVisibility);
        this.markers = this.markers.map(setVisibility);

        if (callback) {
            callback(this.markersOriginal);
        }
    }

    updateOriginalMarker(marker) {
        if (marker.delete) {
            this.markersOriginal = this.markersOriginal.filter(mark => (mark._id !== marker._id) || (mark._id === marker._id && !marker.delete)); // removing to marker delete = true
        } else {
            let isFound = false;
            this.markersOriginal = this.markersOriginal.map((mark) => {
                if (mark._id === marker._id) {
                    // Track manual updates for squad and teammember markers to prevent Firebase flicker
                    if ((marker.markerType === 'squad' || marker.markerType === 'teammember') && marker.lat && marker.lng) {
                        this.recentlyUpdatedMarkers.set(marker._id, {
                            timestamp: Date.now(),
                            position: { lat: marker.lat, lng: marker.lng }
                        });
                    }
                    mark = marker;
                    isFound = true;
                }
                return mark;
            });
            if (!isFound) {
                // Track manual updates for new markers too
                if ((marker.markerType === 'squad' || marker.markerType === 'teammember') && marker.lat && marker.lng) {
                    this.recentlyUpdatedMarkers.set(marker._id, {
                        timestamp: Date.now(),
                        position: { lat: marker.lat, lng: marker.lng }
                    });
                }
                this.markersOriginal.push(marker);
            }
        }
    }
    transform(value: string, args: string[]): string {
        let limit = args.length > 0 ? parseInt(args[0], 10) : 10;
        let trail = args.length > 1 ? args[1] : '...';
        return value.length > limit ? value.substring(0, limit) + trail : value;
    }
    showMarkers(typeToHide: string[] = [], calculateCenterZoom: boolean = true) {
        this.adjustPoints();
        this.unTrackMarkers(this.trackingMarkers);
        this.markers = [];
        // this.usersMarkers = [];
        let markersOriginal = JSON.parse(JSON.stringify(this.markersOriginalAdjusted));
        if (typeToHide.length > 0) {
            markersOriginal = markersOriginal.filter((itm) => !typeToHide.includes(itm.markerType));
        }
        this.markers = markersOriginal;
        let markersToCalculate = JSON.parse(JSON.stringify(markersOriginal));
        markersToCalculate = markersToCalculate.filter((mark) => mark.lat && mark.lat !== '' && mark.lng && mark.lng !== '' && !(this.enableTrackMarkers && this.trackingMarkers.includes(mark.markerType)));
        if (calculateCenterZoom && markersToCalculate.length > 0) {
            // this.calculateCenter(markersToCalculate);
            this.calculateZoomLevel(markersToCalculate);
        }
        // Clean up old entries from recentlyUpdatedMarkers
        this.cleanupRecentlyUpdatedMarkers();
        this.trackMarkers(this.trackingMarkers);
    }

    private cleanupRecentlyUpdatedMarkers() {
        const now = Date.now();
        for (const [markerId, updateInfo] of this.recentlyUpdatedMarkers.entries()) {
            if (now - updateInfo.timestamp > this.MANUAL_UPDATE_TIMEOUT) {
                this.recentlyUpdatedMarkers.delete(markerId);
            }
        }
    }

    trackMarkers(markerTypes = []) {
        if (this.enableTrackMarkers) {
            if (markerTypes.length > 0) {
                this.markers.forEach(mark => {
                    if (markerTypes.includes(mark.markerType)) {
                        let positionPath = undefined;
                        if (mark.markerType === 'investigator-group') {
                            positionPath = 'users-group';
                        } else if (mark.markerType === 'investigator') {
                            positionPath = 'users';
                        }
                        if (mark._id && positionPath) {
                            const markerId = mark._id;
                            const listenerKey = `${positionPath}/${markerId}`;
                            const subject = `live.${positionPath}.${markerId}`;

                            // If there is an existing listener for this key, unsubscribe first
                            if (this.realtimeListeners.has(listenerKey)) {
                                const existingSubject = this.realtimeListeners.get(listenerKey) as string;
                                this.natsMessaging.unsubscribe('', existingSubject);
                                this.realtimeListeners.delete(listenerKey);
                            }

                            this.realtimeListeners.set(listenerKey, subject);

                            // Subscribe to NATS subject for this marker
                            this.natsMessaging.subscribe('', subject, (payload: any) => {
                                const data = payload?.data || payload;
                                const mainData = data;

                                if (mainData && mainData.position && mainData.active) {
                                    const positionData = mainData.position;

                                    if (positionData && positionData.lat && positionData.lat !== '' && positionData.lng && positionData.lng !== '') {
                                        const recentUpdate = this.recentlyUpdatedMarkers.get(mark._id);
                                        const shouldIgnoreUpdate = recentUpdate &&
                                            (Date.now() - recentUpdate.timestamp < this.MANUAL_UPDATE_TIMEOUT) &&
                                            mark.position &&
                                            mark.position.lat &&
                                            mark.position.lng;

                                        if (shouldIgnoreUpdate) {
                                            const currentPos = new google.maps.LatLng(mark.position.lat, mark.position.lng);
                                            const natsPos = new google.maps.LatLng(positionData.lat, positionData.lng);
                                            const distance = google.maps.geometry.spherical.computeDistanceBetween(currentPos, natsPos);

                                            if (distance <= 10) {
                                                // Ignore small echo updates
                                                return;
                                            }
                                        }

                                        mark.position = positionData;
                                        mark.geolocation = JSON.stringify(positionData);
                                        mark.lat = positionData.lat;
                                        mark.lng = positionData.lng;
                                    }
                                    if (mark.markerType === 'squad') {
                                        mark.name = mainData.name || "";
                                        mark.label = mainData.name || "";
                                        mark.label = this.prepareLabel(mark);
                                        mark.description = mainData.description || "";
                                    }
                                    mark.hideIt = false;
                                } else {
                                    mark.hideIt = true;
                                }
                            });
                        }
                    }
                });
            }
        }
    }
    hideMark(markId) {
        this.markers.map((mark) => {
            if (mark.markerType === 'investigator' && mark._id == markId) {
                mark.hideIt = true
            }
        });
    }
    hideMarkGroup(markId) {
        this.markers.map((mark) => {
            if (mark.markerType === 'investigator-group' && mark['loggedInUser'] && mark['loggedInUser'] == markId) {
                mark.hideIt = true
            }
        });
    }
    centerUpdatedMarkers() {
        let markersToCalculate = JSON.parse(JSON.stringify(this.markers));
        markersToCalculate = markersToCalculate.filter((mark) => mark.lat && mark.lat !== '' && mark.lng && mark.lng !== '');
        if (markersToCalculate.length > 0) {
            // this.calculateCenter(markersToCalculate);
            this.calculateZoomLevel(markersToCalculate);
        }
    }
    unTrackMarkers(markerTypes = []) {
        if (this.enableTrackMarkers && markerTypes.length > 0) {
            this.markers.forEach(mark => {
                if (markerTypes.includes(mark.markerType)) {
                    let positionPath = undefined;
                    if (mark.markerType === 'investigator-group') {
                        positionPath = 'users-group';
                    } else if (mark.markerType === 'investigator') {
                        positionPath = 'users';
                    }
                    if (mark._id && positionPath) {
                        const listenerKey = `${positionPath}/${mark._id}`;
                        if (this.realtimeListeners.has(listenerKey)) {
                            const subject = this.realtimeListeners.get(listenerKey) as string;
                            this.natsMessaging.unsubscribe('', subject);
                            this.realtimeListeners.delete(listenerKey);
                        }
                    }
                }
            });
        }
    }
    calculateCenter(markersToCalculate: { position: { lat: number, lng: number } }[]) {
        if (!markersToCalculate?.length) return;

        const latSum = markersToCalculate.map(({ position: { lat } }) => lat).reduce((sum, lat) => sum + lat, 0);
        const lngSum = markersToCalculate.map(({ position: { lng } }) => lng).reduce((sum, lng) => sum + lng, 0);

        this.center = {
            lat: latSum / markersToCalculate.length,
            lng: lngSum / markersToCalculate.length
        };

        this.mapDragGeo.emit(this.center);
    }
    calculateZoomLevelOld(markersToCalculate) {
        var bounds = new google.maps.LatLngBounds();
        for (var i in markersToCalculate) { // your marker list here
            if (markersToCalculate[i].position && markersToCalculate[i].position.hasOwnProperty('lat') && markersToCalculate[i].position.hasOwnProperty('lng')) {
                bounds.extend(markersToCalculate[i].position) // your marker position, must be a LatLng instance
            }
        }
        if (this.markersElem) {
            this.markersElem.fitBounds(bounds, 75);
        }
    }
    calculateZoomLevel(markersToCalculate: { position: { lat: number, lng: number } }[]) {
        if (!markersToCalculate?.length || !this.markersElem) return;

        const bounds = new google.maps.LatLngBounds();

        markersToCalculate.forEach(({ position }) => {
            if (position?.lat !== undefined && position?.lng !== undefined) {
                bounds.extend(new google.maps.LatLng(position.lat, position.lng));
            }
        });

        this.markersElem.fitBounds(bounds, 75);
    }
    reverseGeocode(lat: number, lng: number) {
        if (lat == null || lng == null) {
            return;
        }

        // English / default
        this.geocodingService.resolveAddress(lat, lng, 'en')
            .then(address => {
                if (address) {
                    this.addressPinData.emit(address);
                }
            })
            .catch(() => { });

        // Japanese version (if available)
        this.geocodingService.resolveAddress(lat, lng, 'ja')
            .then(address => {
                if (address) {
                    this.addressJPPinData.emit(address);
                }
            })
            .catch(() => { });
    }
    adjustPoints(): void {
        this.markersOriginalAdjusted = [];
        const minDistance = 4;
        for (let i = 0; i < this.markersOriginal.length; i++) {
            if (this.markersOriginal[i].lat && this.markersOriginal[i].lat !== '' && this.markersOriginal[i].lng && this.markersOriginal[i].lng !== '') {
                let adjusted = false;
                do {
                    adjusted = false;
                    for (let j = 0; j < this.markersOriginalAdjusted.length; j++) {
                        const mark1 = new google.maps.LatLng(this.markersOriginal[i].position.lat, this.markersOriginal[i].position.lng);
                        const mark2 = new google.maps.LatLng(this.markersOriginalAdjusted[j].position.lat, this.markersOriginalAdjusted[j].position.lng);
                        const distance = google.maps.geometry.spherical.computeDistanceBetween(mark1, mark2);
                        if (distance < minDistance) {
                            adjusted = true;
                            this.markersOriginal[i] = this.moveMarkerRandomly(this.markersOriginal[i]);
                            break; // Exit the inner loop to recheck all points from the beginning
                        }
                    }
                } while (adjusted);
            }
            this.markersOriginalAdjusted.push(this.markersOriginal[i]);
        }
    }
    togglePath(id: string, visible: boolean = true) {
        const polylineEntry = this.polylines.get(id);
        if (polylineEntry) {
            polylineEntry.line.setVisible(visible);
            polylineEntry.pointMarkers.forEach((marker) => marker.setVisible(visible));
        }
    }
    // removePath(id: string) {
    //     const polyline = this.polylines.get(id);
    //     if (polyline) {
    //         polyline.setMap(null);
    //         this.polylines.delete(id);
    //     }
    // }

    clearPaths() {
        this.polylines.forEach((value, id) => {
            const polylineEntry = this.polylines.get(id);
            if (polylineEntry) {
                polylineEntry.line.setMap(null);
                polylineEntry.pointMarkers.forEach((marker) => marker.setMap(null));
                this.polylines.delete(id);
            }
        });
        this.polylines = new Map();
    }
    addPath(paths, id, color: string = '#11345a') {
        const polylineEntry = this.polylines.get(id);
        if (polylineEntry) {
            polylineEntry.line.setMap(null);
            polylineEntry.pointMarkers.forEach((marker) => marker.setMap(null));
            this.polylines.delete(id);
        }
        const isDraftPolyline = this.draftSafeRoutePolylineIds.has(id);
        const line = new google.maps.Polyline({
            path: paths,
            geodesic: true,
            strokeColor: color,
            strokeOpacity: isDraftPolyline ? 0.75 : 0,
            strokeWeight: isDraftPolyline ? 7 : 0,
            visible: true,
            zIndex: isDraftPolyline ? 1000 : 10,
            icons: [
                {
                    icon: {
                        ...this.lineSymbol,
                        scale: isDraftPolyline ? 7 : this.lineSymbol.scale
                    },
                    offset: "0",
                    repeat: isDraftPolyline ? "16px" : "20px",
                },
            ],
        });

        line.setMap(this.markersElem.googleMap);
        if (typeof id === 'string' && id.startsWith('safe-route-')) {
            line.addListener('click', () => {
                this.shapeClick.emit({
                    type: 'safe-route',
                    id,
                    coordinates: (paths || []).map((point: any) => ({
                        lat: Number(point?.lat),
                        lng: Number(point?.lng ?? point?.lon)
                    })).filter((p: any) => Number.isFinite(p.lat) && Number.isFinite(p.lng)),
                    color
                });
            });
        }
        const showVertexCircles = isDraftPolyline || (typeof id === 'string' && id.startsWith('safe-route-'));
        const pointMarkers = showVertexCircles ? (paths || []).map((point: any) => {
            const lat = Number(point?.lat);
            const lng = Number(point?.lng ?? point?.lon);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                return null;
            }
            return new google.maps.Marker({
                position: { lat, lng },
                map: this.markersElem.googleMap,
                draggable: isDraftPolyline,
                icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: isDraftPolyline ? 7 : 6,
                    fillColor: color,
                    fillOpacity: 1,
                    strokeColor: '#ffffff',
                    strokeWeight: 2
                }
            });
        }).filter((marker: any) => !!marker) : [];
        if (typeof id === 'string' && id.startsWith('safe-route-')) {
            pointMarkers.forEach((marker: any) => {
                marker.addListener('click', () => {
                    this.shapeClick.emit({
                        type: 'safe-route',
                        id,
                        coordinates: (paths || []).map((point: any) => ({
                            lat: Number(point?.lat),
                            lng: Number(point?.lng ?? point?.lon)
                        })).filter((p: any) => Number.isFinite(p.lat) && Number.isFinite(p.lng)),
                        color
                    });
                });
            });
        }
        if (isDraftPolyline) {
            const emitDraftCoordinates = (interaction: 'vertex-drag-end' | 'vertex-contextmenu', pointIndex: number) => {
                this.shapeClick.emit({
                    type: 'polyline-draft',
                    id,
                    interaction,
                    pointIndex,
                    coordinates: pointMarkers.map((polyPointMarker: any) => {
                        const position = polyPointMarker?.getPosition?.();
                        return {
                            lat: Number(position?.lat?.()),
                            lng: Number(position?.lng?.())
                        };
                    }).filter((coord: any) => Number.isFinite(coord.lat) && Number.isFinite(coord.lng))
                });
            };
            pointMarkers.forEach((marker: any, index: number) => {
                marker.addListener('dragend', () => emitDraftCoordinates('vertex-drag-end', index));
                marker.addListener('rightclick', () => emitDraftCoordinates('vertex-contextmenu', index));
            });
        }
        // // setTimeout(() => line.setMap(null), 2000)
        // this.polylinesMain.push(paths)
        this.polylines.set(id, { line, pointMarkers });
    }
    removePath(id: string) {
        const polylineEntry = this.polylines.get(id);
        if (polylineEntry) {
            polylineEntry.line.setMap(null);
            polylineEntry.pointMarkers.forEach((marker) => marker.setMap(null));
            this.polylines.delete(id);
        }
    }

    dragToLocation(markers, geolocation: { lat: any, lng: any }) {
        if (geolocation.lat != undefined && geolocation.lng != undefined) {
            const latLng = new google.maps.LatLng(geolocation.lat, geolocation.lng);
            this.markersElem.googleMap.panTo(latLng);

            if (markers) {
                this.markersOriginal = [];

                for (let mark of markers) {
                    let marker = this.prepareMarker(mark);
                    this.markersOriginal.push(marker);
                }

                this.adjustPoints();
                this.unTrackMarkers(['investigator', 'investigator-group']);
                this.markers = [];
                let markersOriginal = JSON.parse(JSON.stringify(this.markersOriginalAdjusted));

                this.markers = markersOriginal;
                this.trackMarkers(['investigator', 'investigator-group']);
            }

            this.markersElem.googleMap.setZoom(16);
        }
    }

    updateAndDragMarkers(newMarkers = []) {
        for (let mark of newMarkers) {
            let marker = this.prepareMarker(mark);
            this.updateOriginalMarker(marker);
        }

        this.adjustPoints();
        this.unTrackMarkers(['investigator', 'investigator-group']);
        this.markers = [];
        let markersOriginal = JSON.parse(JSON.stringify(this.markersOriginalAdjusted));

        this.markers = markersOriginal;

        this.markersElem.googleMap.setZoom(16);
    }
    searchByText(text: string, options?: { emitMapSelect?: boolean }) {
        const emitMapSelect = options?.emitMapSelect !== false;
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ address: text }, (results, status) => {
            if (status === 'OK') {
                if (results[0]) {
                    let lat = results[0].geometry.location.lat();
                    let lng = results[0].geometry.location.lng();
                    this.center = {
                        lat: lat,
                        lng: lng
                    }
                    if (emitMapSelect) {
                        this.mapSelect.emit({ lat: lat, lng: lng });
                    }
                    // console.log('searchByText:', { lat: lat, lng: lng });
                    // You can now use the address data as needed.
                } else {
                    console.error('No address found');
                }
            } else {
                console.error('Geocoder failed due to:', status);
            }
        });
    }

    clearNonExistingMarkers(markerType: string, currentMarkers: any[]): void {
        if (!markerType || !currentMarkers) {
            return;
        }

        // Create a Set of current marker IDs for quick lookup
        const currentMarkerIds = new Set(
            currentMarkers
                .filter(m => m && m._id)
                .map(m => m._id)
        );

        // Filter out markers that match the markerType but are not in the current markers array
        const markersToRemove = this.markersOriginal.filter(marker =>
            marker.markerType === markerType &&
            marker._id &&
            !currentMarkerIds.has(marker._id)
        );

        if (markersToRemove.length > 0) {
            // Remove markers from markersOriginal
            const idsToRemove = new Set(markersToRemove.map(m => m._id));
            this.markersOriginal = this.markersOriginal.filter(marker =>
                !(marker.markerType === markerType && marker._id && idsToRemove.has(marker._id))
            );

            // Refresh the display
            this.showMarkersIfReady([], false);
        }
    }

    /**
     * Adds an interactive sector (FOV cone) to the map.
     */
    addSector(id: string, options: {
        center: { lat: number, lng: number },
        radius: number,
        azimuth?: number,
        fov?: number,
        startAngle?: number,
        endAngle?: number,
        title?: string,
        description?: string,
        address?: string,
        editable?: boolean,
        color?: string,
        data?: any,
        showDots?: boolean
    }) {
        if (!this.markersElem || !this.markersElem.googleMap) return;
        const map = this.markersElem.googleMap;
        const title = options.title || id;
        const tooltipContent = `<div style="color: black; padding: 5px;">
            <div style="font-weight: bold; margin-bottom: 4px;">${options.description || ''}</div>
            <div style="font-size: 12px;">${options.address || ''}</div>
        </div>`;

        if (this.sectors.has(id)) {
            this.removeSector(id);
        }
        if (this.boxes.has(id)) {
            this.removeBox(id);
        }

        let azimuth = options.azimuth || 0;
        let fov = options.fov || 60;

        // If start/end angles are provided, calculate azimuth and fov
        if (options.startAngle !== undefined && options.endAngle !== undefined) {
            let diff = options.endAngle - options.startAngle;
            if (diff < 0) diff += 360; // Handle wrapping
            fov = diff;
            azimuth = (options.startAngle + diff / 2) % 360;
        }

        const sector = {
            id: id,
            center: options.center,
            radius: options.radius,
            azimuth: azimuth,
            fov: fov,
            polygon: undefined,
            centerMarker: undefined,
            controlMarker: undefined
        };

        this.sectors.set(id, sector);

        const coordinates = GeoUtils.calculateSectorPolygon(
            sector.center,
            sector.radius,
            sector.azimuth,
            sector.fov
        );
        const path = coordinates.map(coord => ({ lat: coord[1], lng: coord[0] }));

        sector.polygon = new google.maps.Polygon({
            paths: path,
            strokeColor: options.color || '#FF0000',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: options.color || '#FF0000',
            fillOpacity: 0.2,
            map: map,
            clickable: !!title // Enable click/hover if title exists
        });

        sector.polygon.addListener('click', (e: any) => {
            if (e) e.stop(); // Stop propagation
            this.shapeClick.emit(options.data || id);
        });

        if (this.isHoverable && (options.description || options.address)) {
            const infoWindow = new google.maps.InfoWindow({
                content: tooltipContent,
                disableAutoPan: true
            });

            sector.polygon.addListener('mouseover', (e: any) => {
                infoWindow.setPosition(e.latLng);
                infoWindow.open(map);
            });

            sector.polygon.addListener('mousemove', (e: any) => {
                infoWindow.setPosition(e.latLng);
            });

            sector.polygon.addListener('mouseout', () => {
                infoWindow.close();
            });
        }

        // Center Marker (Draggable)
        if (options.editable || options.showDots) {
            sector.centerMarker = new google.maps.Marker({
                position: options.center,
                map: map,
                draggable: true,
                title: options.title || id, // tooltip
                icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 6, // Visual dot size
                    fillColor: options.color || '#FF0000',
                    fillOpacity: 0.5,
                    strokeColor: 'white',
                    strokeWeight: 2
                },
                zIndex: 0
            });

            sector.centerMarker.addListener('click', () => {
                this.shapeClick.emit(options.data || id);
            });

            sector.centerMarker.addListener('drag', (event: any) => {
                const lat = event.latLng.lat();
                const lng = event.latLng.lng();
                sector.center = { lat: lat, lng: lng };
                this.updateSector(id);
                this.updateSectorControlMarker(id);
            });
        }
        // Control Marker (Rotate & Resize)
        if (options.editable) {
            const radius = sector.radius;
            const controlPos = GeoUtils.destinationPoint(options.center.lat, options.center.lng, radius, sector.azimuth);

            sector.controlMarker = new google.maps.Marker({
                position: controlPos,
                map: map,
                draggable: true,
                icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 5,
                    fillColor: 'white',
                    fillOpacity: 1,
                    strokeColor: 'red',
                    strokeWeight: 2
                },
                zIndex: 0
            });

            sector.controlMarker.addListener('click', () => {
                this.shapeClick.emit(options.data || id);
            });

            sector.controlMarker.addListener('drag', (event: any) => {
                const lat = event.latLng.lat();
                const lng = event.latLng.lng();
                const currentLatLng = { lat: lat, lng: lng };
                const center = sector.center;

                const distance = GeoUtils.getDistance(center, currentLatLng);
                sector.radius = distance;

                const bearing = GeoUtils.getBearing(center, currentLatLng);
                sector.azimuth = bearing;

                this.updateSector(id);
            });
        }
    }

    private updateSector(id: string) {
        const sector = this.sectors.get(id);
        if (!sector || !sector.polygon) return;

        requestAnimationFrame(() => {
            const currentSector = this.sectors.get(id);
            if (!currentSector || !currentSector.polygon) return;
            const coordinates = GeoUtils.calculateSectorPolygon(
                currentSector.center,
                currentSector.radius,
                currentSector.azimuth,
                currentSector.fov
            );
            const path = coordinates.map(coord => ({ lat: coord[1], lng: coord[0] }));
            currentSector.polygon.setPaths(path);
        });
    }

    private updateSectorControlMarker(id: string) {
        const sector = this.sectors.get(id);
        if (!sector || !sector.controlMarker) return;
        const rotationPos = GeoUtils.destinationPoint(
            sector.center.lat,
            sector.center.lng,
            sector.radius,
            sector.azimuth
        );
        sector.controlMarker.setPosition(rotationPos);
    }

    /**
     * Adds an interactive box to the map.
     */
    addBox(id: string, options: { center: { lat: number, lng: number }, width: number, height: number, azimuth: number, title?: string, description?: string, address?: string, editable?: boolean, color?: string, data?: any, showDots?: boolean }) {
        if (!this.markersElem || !this.markersElem.googleMap) return;
        const map = this.markersElem.googleMap;
        const title = options.title || id;
        const tooltipContent = `<div style="color: black; padding: 5px;">
            <div style="font-weight: bold; margin-bottom: 4px;">${options.description || ''}</div>
            <div style="font-size: 12px;">${options.address || ''}</div>
        </div>`;

        if (this.boxes.has(id)) {
            this.removeBox(id);
        }
        if (this.sectors.has(id)) {
            this.removeSector(id);
        }

        const box = {
            id: id,
            center: options.center,
            width: options.width,
            height: options.height,
            azimuth: options.azimuth,
            polygon: undefined,
            centerMarker: undefined,
            controlMarker: undefined
        };

        this.boxes.set(id, box);

        const coordinates = GeoUtils.calculateBoxPolygon(
            box.center,
            box.width,
            box.height,
            box.azimuth
        );
        const path = coordinates.map(coord => ({ lat: coord[1], lng: coord[0] }));

        box.polygon = new google.maps.Polygon({
            paths: path,
            strokeColor: options.color || '#0000FF',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: options.color || '#0000FF',
            fillOpacity: 0.2,
            map: map,
            clickable: !!title
        });

        box.polygon.addListener('click', () => {
            this.shapeClick.emit(options.data || id);
        });

        if (this.isHoverable && (options.description || options.address)) {
            const infoWindow = new google.maps.InfoWindow({
                content: tooltipContent,
                disableAutoPan: true
            });

            box.polygon.addListener('mouseover', (e: any) => {
                infoWindow.setPosition(e.latLng);
                infoWindow.open(map);
            });

            box.polygon.addListener('mousemove', (e: any) => {
                infoWindow.setPosition(e.latLng);
            });

            box.polygon.addListener('mouseout', () => {
                infoWindow.close();
            });
        }

        // Center Marker
        if (options.showDots || options.editable) {
            box.centerMarker = new google.maps.Marker({
                position: options.center,
                map: map,
                draggable: !!options.editable,
                title: options.title || id,
                icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 6,
                    fillColor: options.color || '#0000FF',
                    fillOpacity: 0.5,
                    strokeColor: 'white',
                    strokeWeight: 2
                },
                zIndex: 0
            });

            box.centerMarker.addListener('click', () => {
                this.shapeClick.emit(options.data || id);
            });

            if (options.editable) {
                box.centerMarker.addListener('drag', (event: any) => {
                    const lat = event.latLng.lat();
                    const lng = event.latLng.lng();
                    box.center = { lat: lat, lng: lng };
                    this.updateBox(id);
                    this.updateBoxControlMarker(id);
                });
            }

            // Control Marker (Corner)
            const radius = Math.sqrt(Math.pow(options.width / 2, 2) + Math.pow(options.height / 2, 2));
            const angleOffset = GeoUtils.toDegrees(Math.atan2(options.width, options.height));
            const controlPos = GeoUtils.destinationPoint(options.center.lat, options.center.lng, radius, options.azimuth + angleOffset);

            box.controlMarker = new google.maps.Marker({
                position: controlPos,
                map: map,
                draggable: !!options.editable,
                icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 5,
                    fillColor: 'white',
                    fillOpacity: 1,
                    strokeColor: 'blue',
                    strokeWeight: 2
                },
                zIndex: 0
            });

            box.controlMarker.addListener('click', () => {
                this.shapeClick.emit(options.data || id);
            });

            if (options.editable) {
                box.controlMarker.addListener('drag', (event: any) => {
                    const lat = event.latLng.lat();
                    const lng = event.latLng.lng();
                    const currentLatLng = { lat: lat, lng: lng };
                    const center = box.center;

                    const newRadius = GeoUtils.getDistance(center, currentLatLng);
                    const oldRadius = Math.sqrt(Math.pow(box.width / 2, 2) + Math.pow(box.height / 2, 2));
                    const scale = newRadius / oldRadius;

                    box.width *= scale;
                    box.height *= scale;

                    const currentBearing = GeoUtils.getBearing(center, currentLatLng);
                    const cornerAngle = GeoUtils.toDegrees(Math.atan2(box.width, box.height));
                    box.azimuth = (currentBearing - cornerAngle + 360) % 360;

                    this.updateBox(id);
                });
            }
        }
    }

    private updateBox(id: string) {
        const box = this.boxes.get(id);
        if (!box || !box.polygon) return;

        requestAnimationFrame(() => {
            const currentBox = this.boxes.get(id);
            if (!currentBox || !currentBox.polygon) return;
            const coordinates = GeoUtils.calculateBoxPolygon(
                currentBox.center,
                currentBox.width,
                currentBox.height,
                currentBox.azimuth
            );
            const path = coordinates.map(coord => ({ lat: coord[1], lng: coord[0] }));
            currentBox.polygon.setPaths(path);
        });
    }

    private updateBoxControlMarker(id: string) {
        const box = this.boxes.get(id);
        if (!box || !box.controlMarker) return;
        const radius = Math.sqrt(Math.pow(box.width / 2, 2) + Math.pow(box.height / 2, 2));
        const angleOffset = GeoUtils.toDegrees(Math.atan2(box.width, box.height));
        const controlPos = GeoUtils.destinationPoint(
            box.center.lat,
            box.center.lng,
            radius,
            box.azimuth + angleOffset
        );
        box.controlMarker.setPosition(controlPos);
    }

    removeSector(id?: string) {
        if (id) {
            const sector = this.sectors.get(id);
            if (sector) {
                if (sector.polygon) sector.polygon.setMap(null);
                if (sector.centerMarker) sector.centerMarker.setMap(null);
                if (sector.controlMarker) sector.controlMarker.setMap(null);
                this.sectors.delete(id);
            }
        } else {
            this.sectors.forEach((__, key) => this.removeSector(key));
        }
    }

    removeBox(id?: string) {
        if (id) {
            const box = this.boxes.get(id);
            if (box) {
                if (box.polygon) box.polygon.setMap(null);
                if (box.centerMarker) box.centerMarker.setMap(null);
                if (box.controlMarker) box.controlMarker.setMap(null);
                this.boxes.delete(id);
            }
        } else {
            this.boxes.forEach((__, key) => this.removeBox(key));
        }
    }
}
